import React, { useState } from 'react'
import { Stack, Group, Title, Button } from '@mantine/core'
import { DatePickerInput, DatesRangeValue } from '@mantine/dates'
import { IconDownload, IconCalendar } from '@tabler/icons-react'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import Table from '../../../components/AdminDataTable'
import { router } from '@inertiajs/react'
import { AdminReviewsIndexProps } from './type'
import { getColumns } from './columns'
import type { SortParams } from '../../../components/AdminDataTable'
import type { Review } from '../../../types'

dayjs.locale('fr')

const columns = getColumns()

const AdminReviewsIndex: React.FC<AdminReviewsIndexProps> = (props) => {
  const { reviews, pagination, sort, date_from, date_to } = props

  const [dateRange, setDateRange] = useState<DatesRangeValue>([
    date_from ? new Date(date_from) : null,
    date_to ? new Date(date_to) : null,
  ])

  const handleDateChange = (range: DatesRangeValue) => {
    setDateRange(range)
    const [from, to] = range

    if ((from && to) || (!from && !to)) {
      router.get(
        '/admin/reviews',
        {
          date_from: from ? dayjs(from).format('YYYY-MM-DD') : undefined,
          date_to: to ? dayjs(to).format('YYYY-MM-DD') : undefined,
          page: 1,
          per_page: pagination.per_page,
          sort: sort.key,
          order: sort.order,
        },
        { preserveState: true, preserveScroll: true }
      )
    }
  }

  const onPageChange = (page: number) =>
    router.get('/admin/reviews', {
      date_from: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
      date_to: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
      page,
      per_page: pagination.per_page,
      sort: sort.key,
      order: sort.order,
    })

  const onSortChange = (s: SortParams) => {
    const base = {
      date_from: dateRange[0] ? dayjs(dateRange[0]).format('YYYY-MM-DD') : undefined,
      date_to: dateRange[1] ? dayjs(dateRange[1]).format('YYYY-MM-DD') : undefined,
      page: 1,
      per_page: pagination.per_page,
    }
    if (!s.order) {
      router.get('/admin/reviews', base)
    } else {
      router.get('/admin/reviews', { ...base, sort: s.key, order: s.order })
    }
  }

  return (
    <Stack gap="md">
      <Group justify="space-between" align="center">
        <Title order={2}>Avis</Title>
        <Group gap="sm">
          <DatePickerInput
            type="range"
            placeholder="Filtrer par période"
            leftSection={<IconCalendar size={16} />}
            value={dateRange}
            onChange={handleDateChange}
            valueFormat="DD/MM/YYYY"
            clearable
            w={280}
            locale="fr"
          />
          <Button
            variant="light"
            leftSection={<IconDownload size={16} />}
            onClick={() => {
              const params = new URLSearchParams()
              if (dateRange[0]) params.set('date_from', dayjs(dateRange[0]).format('YYYY-MM-DD'))
              if (dateRange[1]) params.set('date_to', dayjs(dateRange[1]).format('YYYY-MM-DD'))
              const query = params.toString()
              window.location.href = `/admin/reviews.xlsx${query ? `?${query}` : ''}`
            }}
          >
            Exporter
          </Button>
        </Group>
      </Group>
      <Table
        columns={columns}
        data={reviews}
        rowKey="id"
        onRowClick={({ record }: { record: Review }) => router.visit(`/admin/reviews/${record.id}`)}
        sortStatus={sort.order ? { columnAccessor: sort.key, direction: sort.order } : undefined}
        onSortStatusChange={(s) => onSortChange({ key: s.columnAccessor as string, order: s.direction })}
        totalRecords={pagination.total_pages * pagination.per_page}
        recordsPerPage={pagination.per_page}
        page={pagination.current_page}
        onPageChange={onPageChange}
      />
    </Stack>
  )
}

export default AdminReviewsIndex
